import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowRight, Clock, Calendar, CheckCircle2, TrendingUp, Zap, BarChart3, Target, Users, Magnet, ArrowLeft, Brain, MousePointer } from 'lucide-react';

export default function AiLeadGenerationArticle() {
  const navigate = useNavigate();
  const [visibleSections, setVisibleSections] = useState(new Set());
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => new Set([...prev, entry.target.dataset.animate]));
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -100px 0px' }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach((section) => observerRef.current.observe(section));

    return () => observerRef.current.disconnect();
  }, []);

  return (
    <div className="bg-white text-zinc-900">
      {/* Back Button */}
      <div className="bg-zinc-50 border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-4">
          <Link to="/resources/articles" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold transition-colors text-sm">
            <ArrowLeft size={16} />
            Back to Articles
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <article className="max-w-4xl mx-auto px-6 lg:px-8 py-12">
        <div data-animate="hero" className={`transition-all duration-1000 ease-out ${
          visibleSections.has('hero') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="inline-block px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold tracking-wider mb-4 rounded">
            LEAD GENERATION
          </div>

          <h1 className="text-3xl lg:text-4xl font-bold mb-4 leading-tight text-zinc-900">
            The Complete Guide to AI-Driven Lead Generation in 2025
          </h1>

          <div className="flex flex-wrap items-center gap-6 text-sm text-zinc-500 mb-8 pb-8 border-b border-zinc-200">
            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-zinc-400" />
              <span>27 December 2024</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock size={16} className="text-zinc-400" />
              <span>10 min read</span>
            </div>
            <div>
              <span className="font-medium">By Digital Trent Team</span>
            </div>
          </div>
        </div>

        {/* Introduction */}
        <section data-animate="intro" className={`mb-12 transition-all duration-1000 ease-out ${
          visibleSections.has('intro') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="prose prose-lg max-w-none text-zinc-700">
            <p className="text-lg leading-relaxed mb-6">
              In 2025, lead generation has fundamentally changed. Gone are the days of cold calling hundreds of prospects, hoping 2-3% convert. <strong className="text-zinc-900">AI-driven lead generation</strong> is now identifying, qualifying, and nurturing your ideal customers automatically—with conversion rates 5-10x higher than traditional methods.
            </p>
            <p className="leading-relaxed mb-6">
              But here's what most small businesses get wrong: they're still using outdated tactics (batch-and-blast emails, generic contact forms, manual follow-ups) whilst competitors leverage AI to deliver personalised, timely outreach at scale.
            </p>
            <p className="leading-relaxed">
              This comprehensive guide reveals exactly how AI-driven lead generation works, the proven strategies that are working right now, and how to implement them without a massive budget or technical team.
            </p>
          </div>
        </section>

        {/* The Lead Gen Problem */}
        <section data-animate="problem" className={`mb-12 transition-all duration-1000 ease-out ${
          visibleSections.has('problem') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-4 text-zinc-900">
            <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <Target size={22} className="text-white" />
            </div>
            Why Traditional Lead Generation Is Broken
          </h2>

          <div className="bg-red-50 border-l-4 border-red-500 p-8 mb-8 rounded-r-lg">
            <p className="text-zinc-700 font-semibold mb-5">The traditional lead generation playbook is failing businesses:</p>
            <ul className="space-y-3 text-zinc-600">
              <li className="flex items-start gap-3">
                <span className="text-red-500 mt-1 text-lg">•</span>
                <span><strong className="text-zinc-900">Low conversion rates:</strong> Average lead-to-customer conversion is just 2.35%</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-red-500 mt-1 text-lg">•</span>
                <span><strong className="text-zinc-900">Generic messaging:</strong> Batch emails get 15-20% open rates (down from 30% in 2020)</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-red-500 mt-1 text-lg">•</span>
                <span><strong className="text-zinc-900">Slow follow-up:</strong> 78% of leads go with the vendor who responds first</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-red-500 mt-1 text-lg">•</span>
                <span><strong className="text-zinc-900">Poor qualification:</strong> Sales teams waste 50% of their time on unqualified leads</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-red-500 mt-1 text-lg">•</span>
                <span><strong className="text-zinc-900">No nurturing:</strong> 79% of marketing leads never convert due to lack of lead nurturing</span>
              </li>
            </ul>
            <p className="text-zinc-900 font-bold mt-6 text-lg">Result: High acquisition costs, low-quality leads, and frustrated sales teams.</p>
          </div>

          <p className="text-zinc-700 leading-relaxed text-lg">
            The problem isn't that lead generation doesn't work—it's that <strong className="text-zinc-900">manual, generic approaches can't compete</strong> in a world where buyers expect personalised, instant responses and relevant content at every touchpoint.
          </p>
        </section>

        {/* How AI Lead Gen Works */}
        <section data-animate="solution" className={`mb-12 transition-all duration-1000 ease-out ${
          visibleSections.has('solution') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-4 text-zinc-900">
            <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <Brain size={22} className="text-white" />
            </div>
            How AI-Driven Lead Generation Actually Works
          </h2>

          <p className="text-lg text-zinc-700 leading-relaxed mb-10">
            AI transforms every stage of lead generation—from identification to conversion. Here's exactly how it works:
          </p>

          <div className="space-y-6 mb-8">
            {[
              {
                num: '1',
                title: 'Intelligent Lead Capture',
                desc: 'AI-powered forms adapt in real-time based on visitor behaviour, asking the right questions at the right time to maximise conversion whilst capturing qualification data.',
                color: 'blue',
                items: ['Smart forms that adjust based on visitor source', 'Conversational chatbots that qualify leads 24/7', 'Social media monitoring for buying signals', 'Website behaviour tracking and intent scoring']
              },
              {
                num: '2',
                title: 'Automatic Lead Qualification',
                desc: 'AI analyses hundreds of data points to score and prioritise leads instantly—no more wasting time on tyre-kickers or unqualified prospects.',
                color: 'green',
                items: ['Behaviour-based lead scoring', 'Company firmographic data enrichment', 'Budget and authority indicators', 'Purchase intent signals', 'Fit score against ideal customer profile']
              },
              {
                num: '3',
                title: 'Personalised Outreach at Scale',
                desc: 'AI generates unique, personalised messages for each lead based on their industry, role, pain points, and engagement history—making every touchpoint feel one-to-one.',
                color: 'purple',
                items: ['Dynamic email personalisation', 'Custom landing pages per visitor segment', 'Personalised video messages', 'Industry-specific value propositions', 'Role-based messaging']
              },
              {
                num: '4',
                title: 'Intelligent Lead Nurturing',
                desc: 'AI automatically sends the right content, at the right time, through the right channel—moving leads through your funnel without manual intervention.',
                color: 'orange',
                items: ['Behavioural trigger campaigns', 'Multi-channel nurture sequences', 'Content recommendations based on interests', 'Re-engagement campaigns for cold leads', 'Optimal send-time prediction']
              },
              {
                num: '5',
                title: 'Predictive Analytics & Optimisation',
                desc: 'AI continuously analyses which tactics work best, predicts which leads will convert, and automatically optimises your campaigns for maximum ROI.',
                color: 'cyan',
                items: ['Conversion probability scoring', 'Channel performance optimisation', 'A/B testing automation', 'Lifetime value prediction', 'Churn risk identification']
              }
            ].map((item, idx) => (
              <div key={idx} className="relative bg-white border border-zinc-200 p-6 rounded-xl hover:border-blue-300 hover:shadow-lg hover:shadow-blue-100/50 transition-all duration-300">
                {/* Faded Micro Grid */}
                <div className="absolute top-0 left-0 w-24 h-24 pointer-events-none opacity-[0.06] overflow-hidden rounded-tl-xl">
                  <div
                    style={{
                      backgroundImage: `linear-gradient(${item.color === 'blue' ? '#3b82f6' : item.color === 'green' ? '#10b981' : item.color === 'purple' ? '#8b5cf6' : item.color === 'orange' ? '#f97316' : '#06b6d4'} 0.5px, transparent 0.5px),
                                        linear-gradient(90deg, ${item.color === 'blue' ? '#3b82f6' : item.color === 'green' ? '#10b981' : item.color === 'purple' ? '#8b5cf6' : item.color === 'orange' ? '#f97316' : '#06b6d4'} 0.5px, transparent 0.5px)`,
                      backgroundSize: '8px 8px',
                      width: '100%',
                      height: '100%'
                    }}
                  />
                </div>

                <div className="flex items-start gap-4 mb-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-lg flex items-center justify-center font-bold text-lg shadow-sm">
                    {item.num}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-zinc-900 mb-2">{item.title}</h3>
                    <p className="text-zinc-700 mb-4 leading-relaxed">{item.desc}</p>
                    <div className="bg-zinc-50 border border-zinc-200 p-4 rounded-lg">
                      <ul className="text-sm text-zinc-600 space-y-2.5">
                        {item.items.map((feature, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <CheckCircle2 size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Real Results */}
        <section data-animate="results" className={`mb-12 transition-all duration-1000 ease-out ${
          visibleSections.has('results') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-4 text-zinc-900">
            <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <BarChart3 size={22} className="text-white" />
            </div>
            Real Results: What AI-Driven Lead Generation Delivers
          </h2>

          <div className="bg-blue-50 border-2 border-blue-200 p-6 rounded-xl mb-6">
            <h3 className="text-xl font-bold mb-4 text-zinc-900">Performance Improvements</h3>
            <div className="grid md:grid-cols-2 gap-5">
              {[
                { label: 'Lead Conversion Rate', old: '2.35%', new: '12-18%', improvement: '5-8x increase' },
                { label: 'Response Time', old: '24-48 hrs', new: 'Instant', improvement: 'Real-time' },
                { label: 'Lead Qualification Time', old: '15-20 min', new: '< 1 min', improvement: '95% faster' },
                { label: 'Cost Per Lead', old: '£45-£75', new: '£8-£15', improvement: '70-85% reduction' }
              ].map((stat, i) => (
                <div key={i} className="bg-white p-5 rounded-lg border border-zinc-200 shadow-sm">
                  <p className="text-sm text-zinc-600 mb-2 font-medium">{stat.label}</p>
                  <p className="text-2xl font-bold text-blue-600 mb-1">{stat.old} → {stat.new}</p>
                  <p className="text-xs text-green-600 font-semibold">{stat.improvement}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Target, title: 'Higher Quality Leads', desc: 'AI qualification ensures sales teams only talk to prospects ready to buy', color: 'blue' },
              { icon: Zap, title: 'Faster Sales Cycles', desc: 'Instant response and nurturing reduces time-to-close by 40-60%', color: 'green' },
              { icon: TrendingUp, title: 'Predictable Pipeline', desc: 'AI forecasting provides accurate revenue predictions 3-6 months out', color: 'purple' }
            ].map((item, i) => (
              <div key={i} className="bg-white border border-zinc-200 p-7 rounded-xl hover:border-blue-300 hover:shadow-md transition-all duration-300">
                <item.icon className="text-blue-600 mb-4" size={36} />
                <h4 className="font-bold text-xl mb-3 text-zinc-900">{item.title}</h4>
                <p className="text-sm text-zinc-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Implementation Guide */}
        <section data-animate="started" className={`mb-12 transition-all duration-1000 ease-out ${
          visibleSections.has('started') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-4 text-zinc-900">
            <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <Magnet size={22} className="text-white" />
            </div>
            Implementing AI-Driven Lead Generation
          </h2>

          <p className="text-lg text-zinc-700 leading-relaxed mb-10">
            Here's your step-by-step roadmap to implementing AI-driven lead generation:
          </p>

          <div className="space-y-5 mb-8">
            {[
              { num: '1', title: 'Define Your Ideal Customer Profile', desc: 'Document your perfect customer: industry, company size, role, pain points, and buying triggers. AI needs this to identify similar prospects.' },
              { num: '2', title: 'Set Up Lead Capture Points', desc: 'Implement AI-powered forms, chatbots, and landing pages across your website and social channels.' },
              { num: '3', title: 'Configure Lead Scoring', desc: 'Define what makes a lead "hot" based on demographics, behaviour, and engagement. AI will learn and refine over time.' },
              { num: '4', title: 'Create Nurture Sequences', desc: 'Build multi-touch campaigns for different segments. AI will personalise and optimise delivery automatically.' },
              { num: '5', title: 'Integrate with Your CRM', desc: 'Connect AI lead generation to your CRM so qualified leads flow directly to sales with full context.' }
            ].map((step, i) => (
              <div key={i} className="flex gap-5 bg-white border border-zinc-200 p-6 rounded-xl hover:border-blue-300 hover:shadow-md transition-all duration-300">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg shadow-sm">{step.num}</div>
                <div className="flex-1">
                  <h4 className="font-bold text-xl text-zinc-900 mb-2">{step.title}</h4>
                  <p className="text-zinc-600 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-green-50 border-l-4 border-green-600 p-8 rounded-r-xl">
            <p className="font-bold text-zinc-900 mb-3 text-lg">Expected Timeline: 2-3 Weeks to Full Implementation</p>
            <p className="text-zinc-700 leading-relaxed">Most businesses see qualified leads flowing within 48 hours of launch, with full optimisation achieved after 30-60 days of AI learning.</p>
          </div>
        </section>

        {/* Conclusion */}
        <section data-animate="conclusion" className={`mb-12 transition-all duration-1000 ease-out ${
          visibleSections.has('conclusion') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-2xl md:text-3xl font-bold mb-6 text-zinc-900">The Bottom Line</h2>

          <div className="text-zinc-700 leading-relaxed space-y-6">
            <p className="text-lg">
              AI-driven lead generation isn't just faster and cheaper—it's fundamentally more effective because it delivers what buyers actually want: <strong className="text-zinc-900">personalised, relevant engagement at the exact moment they're ready</strong>.
            </p>

            <p className="text-lg">
              The businesses winning in 2025 aren't the ones with the biggest marketing budgets. They're the ones using AI to identify high-intent prospects, qualify them instantly, and nurture them with precision that manual processes simply can't match.
            </p>

            <p className="text-lg">
              The question isn't whether AI-driven lead generation works—the data proves it does. The question is: how much growth are you leaving on the table by not implementing it?
            </p>
          </div>
        </section>

        {/* CTA Section */}
        <section data-animate="cta" className={`transition-all duration-1000 ease-out ${
          visibleSections.has('cta') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-8 lg:p-10 rounded-2xl text-center shadow-xl">
            <h3 className="text-2xl md:text-3xl font-bold mb-4 text-white">Ready to 5x Your Lead Conversion Rate?</h3>
            <p className="text-lg text-blue-50 mb-10 max-w-2xl mx-auto leading-relaxed">
              Start generating high-quality leads automatically with Digital Trent's AI-powered lead generation system.
            </p>
            <div className="flex flex-col sm:flex-row gap-5 justify-center">
              <button
                onClick={() => navigate('/auth/signup')}
                className="group bg-white text-blue-600 hover:bg-blue-50 px-10 py-4 rounded-xl font-bold text-lg transition-all duration-300 inline-flex items-center justify-center gap-3 shadow-lg hover:shadow-xl"
              >
                Start Free Trial
                <ArrowRight size={22} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <button
                onClick={() => navigate('/company/contact')}
                className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-10 py-4 rounded-xl font-bold text-lg transition-all duration-300"
              >
                See How It Works
              </button>
            </div>
            <p className="text-sm text-blue-50 mt-6">No credit card required • Full access • Cancel any time</p>
          </div>
        </section>
      </article>
    </div>
  );
}
